/**
 * X-axis time-tick label formatter for the market chart.
 *
 * The `@morfi/chart` Liveline default renders "HH:MM:SS", which collapses to
 * "00:00:00" for every midnight tick in a multi-day window (e.g. the "All"
 * range). This mirrors v1:
 *
 *  - **Multi-day window** (> 24h): axis-grid midnight ticks render as a localized DATE
 *    ("Jun 3" / "3. Juni") so adjacent days are distinguishable — a bare
 *    "HH:MM" is ambiguous about which day. (`@morfi/chart` aligns sub-day ticks
 *    to local midnight so the date tick actually exists; see draw/timeAxis.ts.)
 *  - **Short window** (≤ 5 minutes): "HH:MM:SS" so a rolling live window doesn't
 *    collapse every tick to the same "HH:MM".
 *  - **Otherwise**: "HH:MM".
 *
 * A frozen resolved chart shifts its data into the renderer's current-time
 * coordinate space. `timeOffsetSecs` maps that axis coordinate back to the
 * market's real timestamp while keeping date-tick detection on the aligned
 * renderer grid. Times use the viewer's local timezone and supplied locale.
 */
export function formatTimeAxisLabel(
  axisTimeSecs: number,
  windowSecs: number,
  locale: string,
  timeOffsetSecs: number = 0,
): string {
  const axisDate = new Date(axisTimeSecs * 1000);
  const d = new Date((axisTimeSecs - timeOffsetSecs) * 1000);
  const h = d.getHours().toString().padStart(2, '0');
  const m = d.getMinutes().toString().padStart(2, '0');
  if (windowSecs > 86400 && axisDate.getHours() === 0 && axisDate.getMinutes() === 0) {
    return d.toLocaleDateString(locale, { month: 'short', day: 'numeric' });
  }
  if (windowSecs <= 300) {
    const s = d.getSeconds().toString().padStart(2, '0');
    return `${h}:${m}:${s}`;
  }
  return `${h}:${m}`;
}
